import { useCallback, useEffect, useState } from "react";
import { jfetch } from "../api";

/**
 * CP-14 — Scope / Authority UI: die Antwort auf
 *   „Wo darf SIMO hier lesen? Wo schreiben? Wer besitzt den Writer?
 *    Ist Prod betroffen? Warum ist der Scope verified?"
 * aus den CP-04 Discovery-Records — inklusive der ehrlichen fail-closed-
 * Gründe (warum ein Scope NICHT verifiziert ist). Keine Secrets: Records
 * tragen nur Autoritätswege, Klassen und Beleg-Metadaten.
 */

interface ScopeRecordView {
  scope_key: string;
  scope_family: string;
  discovery_state: "VERIFIED" | "FAIL_CLOSED";
  fail_reason: string | null;
  repo_path: string | null;
  repo_authority: string | null;
  repo_authority_via: string | null;
  repo_remote: string | null;
  repo_default_branch: string | null;
  repo_head: string | null;
  runtime_authority: string | null;
  environment_class: string;
  access_class: string;
  mutation_class: string;
  required_gates_json: string;
  resource_class_json: string;
  capabilities_json: string;
  evidence_json: string;
  verified_at: string | null;
  updated_at: string;
}

export function ScopeAuthorityPanel(): JSX.Element {
  const [records, setRecords] = useState<ScopeRecordView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [offen, setOffen] = useState<string | null>(null);

  const load = useCallback((): void => {
    setLoading(true);
    setError(null);
    jfetch<{ ok: boolean; records: ScopeRecordView[] }>("/api/scopes")
      .then((r) => setRecords(r.records))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const parseList = (json: string): string[] => {
    try {
      const geparst = JSON.parse(json) as unknown;
      return Array.isArray(geparst) ? geparst.map(String) : [];
    } catch {
      return [];
    }
  };

  return (
    <div className="space-y-3" aria-label="Scopes und Autoritaeten" data-testid="scope-authority-panel">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Scopes &amp; Autoritäten</h2>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Lade…" : "Aktualisieren"}
        </button>
      </div>

      {error && (
        <div className="card p-4 text-rose-300 text-sm" role="alert">
          Scopes nicht abrufbar: {error}
        </div>
      )}

      {records.length === 0 && !loading && (
        <div className="card p-4 text-sm text-jarvis-muted">
          Noch keine Scope-Discovery-Records — sie entstehen, sobald Items mit unverifiziertem Scope entdeckt werden.
        </div>
      )}

      <div className="space-y-2">
        {records.map((r) => {
          const verified = r.discovery_state === "VERIFIED";
          const istProd = r.environment_class === "production";
          return (
            <div
              key={r.scope_key}
              className={`card p-3 ${verified ? "" : "border-amber-500/40"}`}
            >
              <button
                className="w-full text-left"
                data-testid={"scope-record-" + r.scope_key}
                onClick={() => setOffen(offen === r.scope_key ? null : r.scope_key)}
                aria-expanded={offen === r.scope_key}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-medium truncate max-w-full">{r.scope_key}</span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                      verified
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        : "border-amber-500/40 bg-amber-500/10 text-amber-300"
                    }`}
                  >
                    {verified ? "VERIFIED" : "FAIL-CLOSED"}
                  </span>
                  {istProd && (
                    <span className="rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-medium text-rose-300">
                      PROD
                    </span>
                  )}
                  <span className="text-[10px] text-jarvis-muted font-mono ml-auto">{r.environment_class}</span>
                </div>
                {verified ? (
                  <div className="text-[11px] text-jarvis-muted mt-1">
                    Zugriff: {r.access_class} · Mutation: {r.mutation_class} · Autorität: {r.repo_authority} ({r.repo_authority_via})
                  </div>
                ) : (
                  <div className="text-[11px] text-amber-200/90 mt-1 truncate" title={r.fail_reason ?? ""}>
                    Grund: {r.fail_reason}
                  </div>
                )}
              </button>
              {offen === r.scope_key && (
                <div className="mt-3 space-y-2 text-xs border-t border-jarvis-border pt-2" data-testid="scope-detail">
                  <div>
                    <strong>Warum {verified ? "verified" : "fail-closed"}?</strong>{" "}
                    {verified
                      ? `Autorität bewiesen über ${r.repo_authority_via} (Head ${r.repo_head?.slice(0, 10) ?? "—"}, Branch ${r.repo_default_branch ?? "—"}); Laufzeit ${r.runtime_authority ?? "—"}; Umgebung ${r.environment_class}.`
                      : `${r.fail_reason} — der Scope bleibt unverifiziert; Items dieses Scopes werden nicht zugelassen (fail-closed).`}
                  </div>
                  {verified && (
                    <>
                      <div>
                        <strong>Lesen/Schreiben:</strong> {r.access_class === "read_write" ? "Lesen + Schreiben (Writer-Lease erforderlich)" : r.access_class} · Remote {r.repo_remote ?? "—"}
                      </div>
                      <div>
                        <strong>Sichere Capabilities:</strong>{" "}
                        {parseList(r.capabilities_json).join(", ") || "—"}
                      </div>
                      <div>
                        <strong>Required Gates:</strong> {parseList(r.required_gates_json).join(", ") || "—"}
                      </div>
                      <div>
                        <strong>Verifiziert am:</strong> {r.verified_at ? new Date(r.verified_at).toLocaleString("de-DE") : "—"}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
